import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, MapPin, Phone, Mail, Clock } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-white to-gray-50 border-t border-gray-100" data-testid="footer">
      {/* Main Footer Content */}
      <div className="container-elegant py-16 px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1 space-y-5">
            <Link to="/" className="inline-block">
              <span className="font-serif text-2xl text-gray-800 tracking-tight">
                Reva <span className="text-malva-dark">Dining</span>
              </span>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              An exceptional culinary journey awaits. Experience fine dining at its most refined.
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-2">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-malva hover:text-white transition-all duration-300"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-malva hover:text-white transition-all duration-300"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-malva hover:text-white transition-all duration-300"
                aria-label="Twitter"
                data-testid="social-twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-5">
            <h4 className="text-gray-800 font-serif text-base font-medium">Explore</h4>
            <ul className="space-y-3">
              {[
                { name: 'Our Menu', path: '/menu' },
                { name: 'Reservations', path: '/booking' },
                { name: 'Order Online', path: '/order' },
                { name: 'About Us', path: '/about' },
                { name: 'Our Blog', path: '/blog' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-500 hover:text-malva-dark transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="space-y-5">
            <h4 className="text-gray-800 font-serif text-base font-medium">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-malva flex-shrink-0 mt-1" />
                <span className="text-gray-500 text-sm leading-relaxed">
                  45-47 York St<br />Twickenham, TW1 3LP
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-malva flex-shrink-0" />
                <a href="tel:+442088929841" className="text-gray-500 hover:text-malva-dark transition-colors duration-300 text-sm">
                  +44 20 8892 9841
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-malva flex-shrink-0" />
                <a href="mailto:bookings@revadining.co.uk" className="text-gray-500 hover:text-malva-dark transition-colors duration-300 text-sm break-all">
                  bookings@revadining.co.uk
                </a>
              </li>
            </ul>
          </div>

          {/* Hours Column */}
          <div className="space-y-5">
            <h4 className="text-gray-800 font-serif text-base font-medium">Opening Hours</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-malva flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-700 text-sm font-medium">Lunch</p>
                  <p className="text-gray-500 text-sm">Mon - Sun: 12:00 - 3:00 PM</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-malva flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-700 text-sm font-medium">Dinner</p>
                  <p className="text-gray-500 text-sm">Mon - Sun: 5:00 - 10:30 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Column */}
          <div className="space-y-5">
            <h4 className="text-gray-800 font-serif text-base font-medium">Scan Menu</h4>
            <div className="inline-flex flex-col items-start">
              <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                <img 
                  src="/reva-menu-qr-code.png" 
                  alt="Scan for Digital Menu"
                  className="w-20 h-20"
                  data-testid="footer-qr-code"
                />
              </div>
              <p className="text-gray-400 text-xs mt-2">Scan for digital menu</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Copyright Section */}
      <div className="border-t border-gray-200 bg-gray-50/50">
        <div className="container-elegant py-6 px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="text-gray-400 text-sm text-center sm:text-left order-2 sm:order-1">
              © {currentYear} Reva Dining. All rights reserved.
            </p>
            
            {/* Legal Links */}
            <div className="flex items-center gap-6 order-1 sm:order-2">
              <Link 
                to="/terms" 
                className="text-gray-400 hover:text-malva-dark transition-colors duration-300 text-sm"
                data-testid="footer-terms"
              >
                Terms & Conditions
              </Link>
              <span className="text-gray-300">|</span>
              <Link 
                to="/privacy" 
                className="text-gray-400 hover:text-malva-dark transition-colors duration-300 text-sm"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
